import { useState } from 'react'

import clsx from 'clsx'

import { WidgetBadgeChoice } from '@/common/util/choice'

import SelectButton from '@/components/view/atoms/button/SelectButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import RadioButtonElement from '@/components/view/atoms/radio/RadioButtonElement'
import { DisclosureSize } from '@/components/view/layouts/disclosure/Disclosure'
import DisclosureWrapper from '@/components/view/layouts/disclosure/DisclosureWrapper'

interface Props<T> {
  value: T
  name: string
  choices: WidgetBadgeChoice<T>[]
  onChange(value: T): void
  disabled?: boolean
  size?: DisclosureSize
  remnant?: boolean
  // 表示幅を親いっぱいに広げるか（テーブルセル等の既定は true）。
  // 他の要素と横並びに置く場合は false にして内容幅に収める
  widthFull?: boolean
}

const DisclosureSelector = <T extends string | number>({
  value,
  name,
  choices,
  disabled,
  onChange,
  size = DisclosureSize.DEFAULT,
  remnant = false,
  widthFull = true
}: Props<T>) => {
  const [showing, setShowing] = useState<boolean>(false)
  const choice = choices.find((choice) => choice.value == value)

  return (
    <DisclosureWrapper
      disclosureNode={
        <>
          {choices.map((choice) => (
            <RadioButtonElement
              key={choice.value}
              name={name}
              value={choice.value}
              checked={choice.value == value}
              label={choice.description ?? choice.name}
              forDisclosure
              onChange={() => {
                onChange(choice.value)
                setShowing(false)
              }}
              disabled={disabled || choice.disabled}
            />
          ))}
        </>
      }
      off={!showing}
      onClickOutSide={() => setShowing(false)}
      size={size}
      widthFull={widthFull}
      remnant={remnant}
    >
      <>
        {choice && choice.badgeType == undefined ? (
          <TextButton disclosure onClick={() => setShowing((showing) => !showing)}>
            {choice ? (choice.description ?? choice.name) : ''}
          </TextButton>
        ) : choice?.combined ? (
          <div
            className={clsx(
              'outline-default group inline-flex items-center rounded-lg whitespace-nowrap group-hover:no-underline focus:appearance-none disabled:pointer-events-none'
            )}
            onClick={() => setShowing(true)}
          >
            <TextButton>{choice ? (choice.description ?? choice.name) : ''}</TextButton>
            <Icon
              type={IconType.DOWN}
              color={disabled ? IconColor.DISABLED : IconColor.EMPHASIS}
              hoverColor={IconColor.EMPHASIS_HOVER}
              activeColor={IconColor.EMPHASIS_ACTIVE}
            />
          </div>
        ) : (
          <SelectButton
            type={choice ? choice.badgeType : undefined}
            onClick={() => setShowing(true)}
          >
            {choice ? (choice.description ?? choice.name) : ''}
          </SelectButton>
        )}
      </>
    </DisclosureWrapper>
  )
}

export default DisclosureSelector
